import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const HomeContainer = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  padding-top: 110px; /* Add padding for fixed menu */

  @media (max-width: 768px) {
    padding-top: 90px; /* Slightly less padding on mobile */
  }
`;

const FeatureImage = styled.div`
  width: 100%;
  margin-bottom: 30px;
  padding-bottom: 30px;

  img {
    width: 100%;
    height: auto;
    object-fit: cover;
    display: block;
  }

  @media (max-width: 768px) {
    img {
      height: auto;
    }
  }
`;

const FeatureCaption = styled.p`
  font-size: 12px;
  color: #666;
  margin: 10px 0 0;
  text-align: left;
`;

const FeaturedHeading = styled.h2`
  font-size: 26px;
  font-weight: bold;
  margin: 0 0 40px 0;
  padding: 0 20px;
  text-align: left;
  padding-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 30px;
  }
`;


const ContentRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-bottom: 40px;
  align-items: center;
  padding-bottom: 40px;
  border-bottom: 1px solid #e0e0e0; /* Light grey line */
  position: relative;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const TextContent = styled.div`
  direction: ltr;
  padding: 20px;
  position: relative;
`;

const ImageContent = styled.div`
  width: 100%;
  padding-top: 75%; /* 4:3 Aspect Ratio (3 / 4 = 0.75) */
  position: relative;
  overflow: hidden;
  direction: ltr;
`;

const ImageLink = styled(Link)`
  position: absolute;
  inset: 0;
  display: block;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }
`;

const Button = styled(Link)`
  display: inline-block;
  padding: 10px 20px;
  background-color: #000;
  color: #fff;
  text-decoration: none;
  margin-top: 20px;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  margin: 0 0 15px;
`;

const Description = styled.p`
  font-size: 16px;
  line-height: 1.5;
  margin-bottom: 20px;
`;

const DateTag = styled.span`
  font-size: 13px;
  color: #666;
  white-space: nowrap;
  position: absolute;
  top: 0;
  right: 0;
  margin: 8px 4px 0 0;
`;

const StudioSection = styled.section`
  padding: 40px 20px 60px;
  text-align: center;
  margin-bottom: 40px;
`;

const StudioLogo = styled.img`
  width: 90px;
  margin: 0 auto 40px;
  display: block;
`;

const StudioText = styled.p`
  font-size: 28px;
  line-height: 1.2;
  margin: 0 auto;
  max-width: 720px;
  color: #333;

  @media (max-width: 768px) {
    font-size: 22px;
  }
`;

const GradientText = styled.span`
  background: linear-gradient(90deg, #2dbf6e 0%, #2b6cff 100%);
  -webkit-background-clip: text;
  color: transparent;
  font-weight: 600;
`;


const Home = () => {
  const newsItems = [
    {
      title: "Shop Opening: Sleepr Studio Storefront",
      description: "An exciting new storefront is now open with exclusive limited edition prints, apparel, and direct access to oil paintings for studio members.",
      image: "/img/shop/sleepr-studio-storefront.png",
      link: "https://shop.sleeprstudio.com",
      external: true,
      buttonText: "Visit Shop",
      date: "Feb 2026"
    },
    {
      title: "Feature Length Documentary: Maps Of Hyperspace",
      description: "A 65-minute documentary following Sleepr's journey through twelve ayahuasca ceremonies in the Peruvian Amazon and the creation of a visual record of inner space.",
      image: "/img/documentary/maps-of-hyperspace.png",
      link: "/news/maps-of-hyperspace-documentary",
      date: "Jan 2026"
    },

    {
      title: "Partnership with the Institute of Contemporary Visionary Art",
      description: "In April 2025, Sleepr joined the Institute of Contemporary Visionary Art, an organization dedicated to developing the academic and philosophical maturity of visionary artists.",
      image: "/img/prints/prints-1.jpg",
      link: "/news/icva-partnership-2025",
      date: "Mar 2025"
    },
    {
      title: "Residency In The Amazon: Peru 2025",
      description: "In October 2024, Sleepr adopted an immersive approach to his art, choosing to live in a remote part of the Amazon with minimal outside contact.",
      image: "/img/show-peru/peru-feature.jpg",
      link: "/news/the-amazon-peru-2025",
      date: "Oct 2024"
    },
    {
      title: "FabUK Magazine: Sleepr London Exhibition Review",
      description: "FabUK recently reviewed the London Secrets exhibition with praise calling it a \"..cultural wonder\".",
      image: "/img/magazine/fabuk-cover.jpg",
      link: "/news/fabuk-magazine-london-exhibition-review",
      date: "Jul 2024"
    },
    {
      title: "Secrets: Sold Out Solo Exhibition in London",
      description: "In May 2024, Sleepr debuted his first solo exhibition of 12 large-scale works at 200cm wide. The show was sold out in advance, before the works were revealed.",
      image: "/img/show-london/sleepr-show-25.jpg",
      link: "/news/solo-exhibition-london-2024",
      date: "Apr 2024"
    },
    {
      title: "Art Basel Miami: Artist Residency",
      description: "In December 2023 Sleepr was one an artist in resident at the prestigious Art Basel Miami Art Week, creating inside a masked performance chamber.",
      image: "/img/show-miami/feature1.png",
      link: "/news/art-basel-miami-2023",
      date: "Dec 2023"
    },
    {
      title: "Sotheby's Hong Kong: Contemporary Discoveries Auction",
      description: "In March 2023, Sleepr was included in the Contemporary Discoveries auction at Sotheby's collection, and was sold at auction in Hong Kong.",
      image: "/img/show-sothebys/feature1.png",
      link: "/news/sothebys-hong-kong-2023",
      date: "Mar 2023"
    }


  ];

  return (
    <HomeContainer>
      <FeatureImage>
        <img src="/img/home/feature.png" alt="Sleepr Studio" />
        <FeatureCaption>Secret Tapes Of The Afterlife (2025).</FeatureCaption>
      </FeatureImage>

      {/* <FeaturedHeading>About</FeaturedHeading> */}
      <StudioSection>
        {/* <StudioLogo src="/img/logo/sleepr-studio-wink-black.png" alt="Sleepr Studio" /> */}
        <StudioText>
        Sleepr is a contemporary artist with a <em>very</em> different mission statement: to manufacture the impossible objects seen on visionary expeditions past the limits of the five senses - aka  <GradientText>the spirit world</GradientText>.
        </StudioText>
      </StudioSection>

      <FeaturedHeading>News</FeaturedHeading>

      {newsItems.map((item, index) => (
        <ContentRow key={index} style={index === 0 ? { borderTop: '1px solid #e0e0e0', paddingTop: '40px' } : {}}>
          <DateTag>{item.date}</DateTag>
          <ImageContent>
            {item.external ? (
              <ImageLink as="a" href={item.link} target="_blank" rel="noreferrer">
                <img src={item.image} alt={item.title} />
              </ImageLink>
            ) : (
              <ImageLink to={item.link}>
                <img src={item.image} alt={item.title} />
              </ImageLink>
            )}
          </ImageContent>
          <TextContent>
            <Title>{item.title}</Title>
            <Description>{item.description}</Description>
            {item.external ? (
              <Button as="a" href={item.link} target="_blank" rel="noreferrer">
                {item.buttonText || 'Read More'}
              </Button>
            ) : (
              <Button to={item.link}>{item.buttonText || 'Read More'}</Button>
            )}
          </TextContent>
        </ContentRow>
      ))}

    </HomeContainer>
  );
};

export default Home; 